import { useMemo, useState } from 'react';
import { AlertTriangle, ChevronDown, ExternalLink, Loader2, RefreshCw, Send, Ban } from 'lucide-react';
import {
  interfacesService, type ErpWarehouse, type InterfaceOrder, type InterfaceOrderDocument,
} from '@/lib/interfacesService';

const selectClass =
  'w-full rounded-lg border border-gray-300 bg-white px-2 py-1.5 text-sm focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-200 disabled:bg-gray-50';

export const DEFAULT_WAREHOUSE_NAME = 'מחסן ברירת המחדל';

function suggestedStorage(order: InterfaceOrder, lineKey: string): number | null {
  const parts = (order.allocation ?? []).filter(p => p.lineKey === lineKey);
  if (!parts.length) return null;
  return parts.reduce((best, p) => (p.quantity > best.quantity ? p : best)).storageId;
}

export function DocumentList({ docs }: { docs: InterfaceOrderDocument[] }) {
  if (!docs.length) return null;
  return (
    <ul className="space-y-1.5">
      {docs.map(d => (
        <li key={d.id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg bg-gray-50 px-3 py-2 text-sm">
          <span className="text-gray-800">
            {d.doc_kind === 'credit' ? 'זיכוי' : 'חשבונית'} {d.external_number ? `#${d.external_number}` : ''} · {d.storage_name || DEFAULT_WAREHOUSE_NAME}
            {d.amount != null && <span className="mr-2 text-gray-500">₪{Number(d.amount).toLocaleString('he-IL')}</span>}
          </span>
          <span className="flex items-center gap-2">
            {d.status === 'failed' && <span className="text-xs text-red-600">{d.error_message || 'נכשל'}</span>}
            {d.status === 'pending' && <span className="text-xs text-amber-600">בתהליך</span>}
            {d.external_url && (
              <a href={d.external_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs font-medium text-sky-700 hover:underline">
                פתיחה <ExternalLink className="h-3 w-3" />
              </a>
            )}
          </span>
        </li>
      ))}
      {docs.find(d => d.split_reason)?.split_reason && (
        <li className="px-1 text-xs text-gray-500">{docs.find(d => d.split_reason)!.split_reason}</li>
      )}
    </ul>
  );
}

export function PendingOrderCard({ order, companyId, warehouses, canEdit, onDone }: {
  order: InterfaceOrder;
  companyId: string;
  warehouses: ErpWarehouse[];
  canEdit: boolean;
  onDone: (message: string) => void;
}) {
  const lines = order.order_payload?.lines ?? [];
  const [open, setOpen] = useState(false);
  const [snapshot, setSnapshot] = useState(order.stock_snapshot ?? {});
  const [assign, setAssign] = useState<Record<string, number | null>>(
    () => Object.fromEntries(lines.map(l => [l.key, suggestedStorage(order, l.key)])),
  );
  const [busy, setBusy] = useState<'stock' | 'issue' | 'cancel' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirmCancel, setConfirmCancel] = useState(false);

  const storageIds = useMemo(() => {
    const ids = new Set<number>();
    for (const row of Object.values(snapshot)) for (const k of Object.keys(row)) ids.add(Number(k));
    for (const w of warehouses) ids.add(w.id);
    return [...ids];
  }, [snapshot, warehouses]);
  const nameOf = (id: number) => warehouses.find(w => w.id === id)?.name ?? (id === 0 ? DEFAULT_WAREHOUSE_NAME : `מחסן ${id}`);
  const stockAt = (sku: string, key: string, id: number) => snapshot[sku || key]?.[String(id)];
  const shortLines = lines.filter(l => !storageIds.some(id => (stockAt(l.sku, l.key, id) ?? 0) >= l.quantity));
  const allAssigned = lines.length > 0 && lines.every(l => assign[l.key] != null);
  const docsCount = new Set(Object.values(assign).filter(v => v != null)).size;

  const refreshStock = async () => {
    setBusy('stock'); setError(null);
    const r = await interfacesService.refreshOrderStock(companyId, order.id);
    setBusy(null);
    if (r.ok) setSnapshot(r.data); else setError(r.error);
  };

  const issue = async () => {
    setBusy('issue'); setError(null);
    const r = await interfacesService.issueOrder(companyId, order.id, assign as Record<string, number>);
    setBusy(null);
    if (r.ok) onDone(`הזמנה ${order.order_name} שודרה — הופקו ${r.data} מסמכים`);
    else setError(r.error);
  };

  const cancel = async () => {
    setBusy('cancel'); setError(null);
    const r = await interfacesService.cancelOrder(companyId, order.id);
    setBusy(null); setConfirmCancel(false);
    if (r.ok) onDone(`הזמנה ${order.order_name} סומנה כטופלה ללא מסמך`);
    else setError(r.error);
  };

  return (
    <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm transition-shadow hover:shadow-md">
      <button type="button" onClick={() => setOpen(o => !o)} className="flex w-full items-start justify-between gap-4 px-5 py-4 text-right">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <p className="font-semibold text-gray-900">הזמנה {order.order_name}</p>
            <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${order.status === 'failed' ? 'bg-red-50 text-red-700' : 'bg-amber-50 text-amber-700'}`}>
              {order.status === 'failed' ? 'ההפקה נכשלה' : 'ממתינה להחלטה'}
            </span>
          </div>
          {order.reason && <p className="mt-1 text-sm text-gray-600">{order.reason}</p>}
          <p className="mt-1 text-xs text-gray-400">{new Date(order.created_at).toLocaleString('he-IL', { dateStyle: 'short', timeStyle: 'short' })} · {lines.length} פריטים</p>
        </div>
        <ChevronDown className={`mt-1 h-5 w-5 shrink-0 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="space-y-4 border-t border-gray-100 px-5 py-4">
          {!!shortLines.length && (
            <p className="flex items-start gap-2 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              אין מספיק מלאי באף מחסן עבור: {shortLines.map(l => l.name).join(', ')}. אפשר עדיין לבחור מחסן — המלאי בו ירד למינוס.
            </p>
          )}

          <div className="overflow-x-auto rounded-xl border border-gray-200">
            <table className="w-full min-w-[520px] text-sm">
              <thead className="bg-gray-50 text-xs text-gray-600">
                <tr>
                  <th className="px-3 py-2 text-right font-medium">פריט</th>
                  <th className="px-3 py-2 text-center font-medium">כמות</th>
                  {storageIds.map(id => <th key={id} className="px-3 py-2 text-center font-medium">{nameOf(id)}</th>)}
                  <th className="w-44 px-3 py-2 text-right font-medium">להוריד מ</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {lines.map(l => (
                  <tr key={l.key}>
                    <td className="px-3 py-2"><p className="font-medium text-gray-800">{l.name}</p>{l.sku && <p className="text-xs text-gray-500">{l.sku}</p>}</td>
                    <td className="px-3 py-2 text-center font-semibold text-gray-800">{l.quantity}</td>
                    {storageIds.map(id => {
                      const q = stockAt(l.sku, l.key, id);
                      const enough = (q ?? 0) >= l.quantity;
                      return (
                        <td key={id} className={`px-3 py-2 text-center ${q == null ? 'text-gray-300' : enough ? 'text-emerald-700' : 'text-red-600'}`}>
                          {q ?? '—'}
                        </td>
                      );
                    })}
                    <td className="px-3 py-2">
                      <select
                        value={assign[l.key] ?? ''}
                        disabled={!canEdit || !!busy}
                        onChange={e => setAssign(a => ({ ...a, [l.key]: e.target.value === '' ? null : Number(e.target.value) }))}
                        className={selectClass}
                        aria-label={`מחסן עבור ${l.name}`}
                      >
                        <option value="">בחרו מחסן</option>
                        {storageIds.map(id => <option key={id} value={id}>{nameOf(id)}</option>)}
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <DocumentList docs={order.interface_order_documents} />

          {error && <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>}

          {canEdit && (
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex flex-wrap gap-2">
                <button
                  type="button" onClick={refreshStock} disabled={!!busy}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:opacity-50"
                >
                  <RefreshCw className={`h-4 w-4 ${busy === 'stock' ? 'animate-spin' : ''}`} /> רענון מלאי
                </button>
                {confirmCancel ? (
                  <span className="inline-flex items-center gap-2 rounded-lg bg-red-50 px-3 py-1.5 text-sm text-red-700">
                    לבטל בלי להפיק מסמך?
                    <button type="button" onClick={cancel} disabled={!!busy} className="font-semibold underline">כן, לבטל</button>
                    <button type="button" onClick={() => setConfirmCancel(false)} className="text-gray-600">לא</button>
                  </span>
                ) : (
                  <button
                    type="button" onClick={() => setConfirmCancel(true)} disabled={!!busy}
                    className="inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-100 disabled:opacity-50"
                  >
                    <Ban className="h-4 w-4" /> בטל הזמנה ללא מסמך
                  </button>
                )}
              </div>
              <button
                type="button" onClick={issue} disabled={!!busy || !allAssigned}
                className="inline-flex items-center gap-2 rounded-lg bg-sky-600 px-5 py-2 text-sm font-semibold text-white transition-colors hover:bg-sky-700 disabled:opacity-50"
              >
                {busy === 'issue' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                שדר להפקת חשבונית{docsCount > 1 ? ` (${docsCount} מסמכים)` : ''}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
